import { faXTwitter } from "@fortawesome/free-brands-svg-icons/faXTwitter";
import { faArrowUpRight } from "@fortawesome/pro-regular-svg-icons/faArrowUpRight";
import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons/faUpRightFromSquare";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Grid, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AxiosResponse } from "axios";
import { MprcCard, MprcChipChoice } from "components";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcRibbonScroll } from "components/MprcRibbonScroll";
import { FeedAttachDto, PublicationAttachType } from "generated/CoreApi_v1";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils from "lib/LibUtils";
import MediaUtils from "lib/MediaUtils";
import * as React from 'react';
import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { LmsPaths } from "routes/LmsRouter";
import { SessionService } from "services/sessionService";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import FeedItemHelper from "../../FeedItemHelper";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function getSourceUrlFromAttach(a: FeedAttachDto) {
    if (a.fileId) {
        return SessionService.current.getAssetUrl(a.fileId);
    }

    return a.linkSourceUrl;
}

function getIconFromAttach(a: FeedAttachDto, avatar?: boolean) {
    switch (a.attachType) {
        case PublicationAttachType.Document:
            if (a.fileName) return avatar
                ? <Avatar src={MediaUtils.getFileIcon(a.fileName)} sx={{ width: 24, height: 24, backgroundColor: "transparent" }} />
                : <img src={MediaUtils.getFileIcon(a.fileName)} width={32} height={32} />;
            break;
        case PublicationAttachType.Twitter:
            return <FontAwesomeIcon icon={faXTwitter} color="#1D9BF0" />;
    }
    return <FontAwesomeIcon icon={faUpRightFromSquare} style={{ backgroundColor: "transparent", color: "#788898" }} />;
}

function getThumbnailFromAttach(a: FeedAttachDto) {
    if (a.fileName) return <img src={MediaUtils.getFileIcon(a.fileName)} width={55} />;
    if (a.linkThumbnailUrl) {
        if (a.attachType == PublicationAttachType.PerformaPublication && a.linkThumbnailUrl != null) {
            return <img src={SessionService.current.getApiFullUrl(a.linkThumbnailUrl)!} style={{ height: "64px", objectFit: "contain" }} />;
        }

        return <img src={a.linkThumbnailUrl} style={{ height: "64px", objectFit: "contain" }} />;
    }
    if (a.attachType == PublicationAttachType.Twitter) return <FontAwesomeIcon icon={faXTwitter} size="3x" color="#1D9BF0" />;
    return null;
}

function getTitleFromAttach(a: FeedAttachDto) {
    return a.fileTitle ?? a.fileName ?? a.linkTitle;
}

function getDescriptionFromAttach(a: FeedAttachDto) {
    if (a.fileId) {
        if (a.fileDescription) return a.fileDescription;
        return null; // Formaters.fileSize(a.fileSize) + " - " + Formaters.localeDate(a.file.metadata.fileCreationDate);
    }
    return a.linkDescription;
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IRenderProps {
    attach: PublicationUnifiedAttach;
    onView: (e: React.SyntheticEvent, item: PublicationUnifiedAttach) => void;
}

const RenderSingle: React.FC<IRenderProps> = ({ attach, onView }) => {
    return (
        <MprcCard
            paperColor={["paper", "dark"]}
            elevation={0}
            sx={{ p: 1, height: 64 }}
        >
            <div
                className={AppGlobalClassKeys.clickable}
                onClick={(e) => onView(e, attach)}
            >
                <Grid container justifyContent="start" alignItems="start" spacing={1} >
                    {
                        attach.thumbnail &&
                        <Grid>
                            {attach.thumbnail}
                        </Grid>
                    }
                    <Grid size="grow">

                        <Grid container justifyContent="center" alignItems="start">
                            <Grid size="grow" maxHeight="64px">
                                <Box sx={{ WebkitLineClamp: 3, display: "-webkit-box", WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                    <MprcFeedTypography variant="body3" title={attach.title}>
                                        {attach.title}
                                    </MprcFeedTypography>
                                    <Typography variant="subtitle2" component="div">
                                        {attach.description}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid>
                                <FontAwesomeIcon icon={faArrowUpRight} fixedWidth />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </div>
        </MprcCard>
    );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IRenderAttachsProps {
    attachs: PublicationUnifiedAttach[];
    onView: (e: React.SyntheticEvent, item: PublicationUnifiedAttach) => void;
}

const RenderAttachs: React.FC<IRenderAttachsProps> = ({ attachs, onView }) => {

    return attachs.length > 0
        ? (
            <MprcRibbonScroll>
                {
                    attachs.map((v, index) => (
                        <MprcChipChoice
                            key={index}
                            avatar={v.icon}
                            label={LibUtils.safeSubstr(v.title, 0, 30, '...')}
                            title={v.title}
                            onClick={(e) => onView(e, v)}
                        />
                    ))
                }
            </MprcRibbonScroll>
        )
        : null;
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface AttachProps {
    publicationId: string;
    attachs: FeedAttachDto[] | null;
    helper: FeedItemHelper;
    onOpen?: (attachId: string) => void;
    onClose?: (attachId: string) => void;
    onMarkRead?: () => Promise<AxiosResponse<boolean, any>>;
}

export const DefaultAttach = () => {
    const { pub: { attachs }, helper, pub, onMarkRead } = useCardContext();
    return <InternalAttach publicationId={pub.publicationId} attachs={attachs} helper={helper} onMarkRead={onMarkRead} />;
};


export const InternalAttach: React.FC<AttachProps> = ({ publicationId, attachs, onOpen, onClose, onMarkRead, helper }) => {

    const navigate = useNavigate();
    const location = useLocation();
    const checkCloseWindowTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    ////////////////////////////////////////////////

    const handleViewAttach = (e: React.SyntheticEvent, attach: PublicationUnifiedAttach) => {
        e.preventDefault();

        async function doOpenWindow() {
            const openedWindow = attach.filename
                ? await MediaUtils.openUrlInNewWindow(attach.filename, attach.sourceUrl, attach.attachId)
                : HtmlUtils.openUrlInNewWindow(attach.sourceUrl, attach.attachId);

            if (openedWindow) {
                // Dispara evento de popup aberto.
                if (onOpen) onOpen(attach.attachId);

                // Monitora quando a janela é fechada.
                checkCloseWindowTimer.current = setInterval(function () {
                    if (openedWindow.closed) {
                        clearInterval(checkCloseWindowTimer.current!);
                        checkCloseWindowTimer.current = null;

                        // Dispara evento de popup fechado.
                        onClose && onClose(attach.attachId);
                    }
                }, 1000);
            }
        }

        // Marca publicação como "lida" ao clicar em qualquer attach.
        if (onMarkRead) onMarkRead();

        if (attach.attachType == PublicationAttachType.PerformaPublication) {
            navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), publicationId } });
            navigate(attach.sourceUrl);
        } else if (attach.attachType == PublicationAttachType.PerformaTraining) {
            navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), publicationId } });
            navigate(LmsPaths.BuildTrainingViewer(attach.objectId!), { state: { "previousUrl": location.pathname } });
        }
        else {
            doOpenWindow();
        }
    };

    const memoizedAttachs = useMemo(() => {

        const localAttachs = helper.nonPresentationAttachs();
        if (localAttachs.length == 0)
            return null;

        const mappedAttachs: PublicationUnifiedAttach[] =
            localAttachs
                .map((v, index) => ({
                    arrIndex: index,
                    attachId: v.attachId,
                    sourceUrl: getSourceUrlFromAttach(v),
                    filename: v.fileName ?? undefined,
                    title: getTitleFromAttach(v),
                    description: getDescriptionFromAttach(v),
                    thumbnail: getThumbnailFromAttach(v),
                    icon: getIconFromAttach(v),
                    attachType: v.attachType,
                    objectId: v.linkExternalId,
                } as PublicationUnifiedAttach))
            ;

        return localAttachs.length == 0
            ? null
            : localAttachs.length == 1 && !helper.hasMedia
                ? <RenderSingle attach={mappedAttachs[0]} onView={handleViewAttach} />
                : <RenderAttachs attachs={mappedAttachs} onView={handleViewAttach} />
            ;
    }, [attachs]);

    ////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////
    useEffect(() => {
        return () => {
            if (checkCloseWindowTimer.current) clearInterval(checkCloseWindowTimer.current);
        };
    }, []);

    ////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////
    return memoizedAttachs;
};
